import { MultiSelect, Select, SimpleGrid, Text } from '@mantine/core';
import { YearPickerInput } from '@mantine/dates';
import type { Plant } from '../../types/entities';
import {
  ReportStatus,
  ReportStatusLabels,
  ReportTypeIdentifier,
  ReportTypeIdentifierLabels,
  type ReportFilters as ReportFiltersType,
  type ReportInterval,
} from '../../types/reports';
import { useAuth } from '../../hooks/useAuth';
import { visibleReportTypes } from '../../utils/reportAccess';
import { REPORT_YEAR_RANGE_START, currentReportYear } from '../../utils/reportYearRange';
import { ReportPeriodSelector } from './ReportPeriodSelector';

interface ReportFiltersProps {
  filters: ReportFiltersType;
  onFiltersChange: (filters: ReportFiltersType) => void;
  plants: Plant[];
  loading?: boolean;
  // Optional (25-04): `ReportsPage.tsx` uebergibt diese Prop erst ab Plan 25-04, wenn die
  // Intervall-Ableitung aus den geladenen Zeilen dort verdrahtet ist. Ohne die Prop verhaelt
  // sich die Leiste wie bisher ohne Perioden-Selektor -- damit bleibt `ReportsPage.tsx` in
  // diesem Plan unangetastet und type-check nach jedem Task gruen.
  periodInterval?: ReportInterval | null;
}

export function ReportFilters({
  filters,
  onFiltersChange,
  plants,
  loading,
  periodInterval,
}: ReportFiltersProps) {
  const { user } = useAuth();

  const plantOptions = [
    { value: '', label: 'Alle Werke' },
    ...plants.map((plant) => ({
      value: String(plant.id),
      label: plant.name,
    })),
  ];

  const statusOptions = Object.values(ReportStatus).map((status) => ({
    value: status,
    label: ReportStatusLabels[status],
  }));

  // D-18: Mehrfachauswahl; die Liste kommt serverseitig ueber `status[]`. Die Altform (Einzelwert
  // als String) wird in ein Ein-Element-Array gewandelt.
  const statusValue = Array.isArray(filters.status)
    ? filters.status
    : filters.status
      ? [filters.status]
      : [];

  // D-10/D-12: einzige Quelle der Typ-Optionen ist die Utility-Funktion aus `reportAccess.ts` --
  // Rolle UND freigeschaltete Kategorie zugleich. Die frueher hier gefuehrte lokale Rollenkette
  // ist ersatzlos entfallen (konsolidiert in `utils/reportAccess.ts`, Plan 25-02).
  const availableTypes = visibleReportTypes(user);

  const typeOptions = [
    { value: '', label: 'Alle Meldungstypen' },
    ...availableTypes.map((type) => ({
      value: type,
      label: ReportTypeIdentifierLabels[type],
    })),
  ];

  const handlePlantChange = (value: string | null) => {
    onFiltersChange({
      ...filters,
      plant: value ? parseInt(value, 10) : undefined,
    });
  };

  const handleStatusChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      status: values.length ? (values as ReportStatus[]) : undefined,
    });
  };

  const handleTypeChange = (value: string | null) => {
    onFiltersChange({
      ...filters,
      reportType: value ? (value as ReportTypeIdentifier) : undefined,
    });
  };

  // Mantine 9 liefert einen YYYY-MM-DD-String oder null (DateValue), kein Date-Objekt -- das
  // Jahr wird ueber die ersten vier Zeichen des Strings gelesen, NICHT ueber eine Umwandlung in
  // ein Date-Objekt und dessen getFullYear() (Zeitzonenfalle, Pitfall 3 aus 25-RESEARCH.md wird
  // so gar nicht erst betreten).
  const handleYearChange = (value: string | null) => {
    if (!value) {
      // Ohne Jahr gibt es laut D-09 keinen Perioden-Selektor -- eine unsichtbare Periode darf
      // nicht still weiterfiltern (D-08), deshalb wird period hier mitgeraeumt.
      onFiltersChange({ ...filters, year: undefined, period: undefined });
      return;
    }
    onFiltersChange({ ...filters, year: Number(value.slice(0, 4)) });
  };

  const handlePeriodChange = (period: string | undefined) => {
    onFiltersChange({ ...filters, period });
  };

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      <Select
        placeholder="Werk"
        data={plantOptions}
        value={filters.plant ? String(filters.plant) : ''}
        onChange={handlePlantChange}
        disabled={loading}
        clearable
      />
      {availableTypes.length === 0 ? (
        // D-11: Frontend-Komfortebene -- kein leeres Dropdown, kein deaktiviertes Select. Die
        // harte Grenze bleibt das Backend, das die Kategorie unabhaengig davon fuer jeden
        // Nutzer prueft (T-25-06).
        <Text size="sm" c="dimmed">
          Für Ihr Konto ist keine Meldungsart freigeschaltet.
        </Text>
      ) : (
        <Select
          placeholder="Meldungstyp"
          data={typeOptions}
          value={filters.reportType ?? ''}
          onChange={handleTypeChange}
          disabled={loading}
          clearable
        />
      )}
      <MultiSelect
        placeholder={statusValue.length === 0 ? 'Alle Status' : undefined}
        data={statusOptions}
        value={statusValue}
        onChange={handleStatusChange}
        disabled={loading}
        clearable
      />
      <YearPickerInput
        placeholder="Jahr"
        value={filters.year ? `${filters.year}-01-01` : null}
        onChange={handleYearChange}
        minDate={`${REPORT_YEAR_RANGE_START}-01-01`}
        maxDate={`${currentReportYear()}-12-31`}
        disabled={loading}
        clearable
        locale="de"
      />
      {periodInterval ? (
        <ReportPeriodSelector
          interval={periodInterval}
          value={filters.period}
          onChange={handlePeriodChange}
          disabled={loading}
        />
      ) : null}
    </SimpleGrid>
  );
}
